import { useState } from 'react';
import { ApiError } from '../lib/api';
import { hasString, useCodeLabel, useI18n } from '../lib/i18n';
import { useApiError } from '../lib/useApiError';
import { formatDateTime, formatGel } from '../lib/format';
import {
  useArchiveRole, useCreateRole, useCreateStaff, usePermissions,
  useRoles, useStaff, useUpdateRole, useUpdateStaff,
} from '../lib/queries';
import { can, useSession } from '../lib/session';
import type { RoleRow, StaffMember } from '../lib/types';
import {
  Alert, Amount, Button, Card, EmptyState, Field, Pill, SectionTitle, Skeleton,
} from '../components/ui';

function RoleChip({ role }: { role: { code?: string; name: string; colorHex: string | null } }) {
  const { roleName } = useRoleText();
  return (
    <span
      className="shrink-0 rounded-md border px-2 py-0.5 text-[0.62rem] font-semibold tracking-wider"
      style={role.colorHex
        ? { color: role.colorHex, borderColor: `${role.colorHex}55`, backgroundColor: `${role.colorHex}14` }
        : undefined}
    >
      {roleName(role.code ?? '', role.name).toUpperCase()}
    </span>
  );
}

/**
 * Permission wording comes from the database in English. Translating it by
 * code here keeps the server monolingual while the screen reads in whichever
 * language the person chose; a code with no translation yet falls back to
 * whatever the server said rather than to nothing.
 */
function usePermissionText() {
  const { t } = useI18n();
  return (code: string, fallback: string) => {
    const key = `perm.${code}`;
    return hasString(key) ? t(key) : fallback;
  };
}

function useRoleText() {
  const { t } = useI18n();
  return {
    roleName: (code: string, fallback: string) => {
      const key = `role.${code}`;
      return hasString(key) ? t(key) : fallback;
    },
    roleDescription: (code: string, fallback: string | null) => {
      const key = `roleDesc.${code}`;
      return hasString(key) ? t(key) : (fallback ?? '');
    },
  };
}

function PermissionPicker({
  selected, onToggle, grantable,
}: { selected: Set<string>; onToggle: (code: string) => void; grantable: Set<string> }) {
  const permissionText = usePermissionText();
  const code = useCodeLabel();
  const { data } = usePermissions();
  if (!data) return <Skeleton className="h-40" />;

  return (
    <div className="max-h-72 space-y-3 overflow-y-auto rounded-xl border border-line bg-raised/30 p-3">
      {data.groups.map((group) => (
        <div key={group.group}>
          <div className="text-[0.62rem] font-semibold uppercase tracking-wider text-ink-faint">
            {code('permGroup', group.group)}
          </div>
          <div className="mt-1.5 space-y-1">
            {group.permissions.map((permission) => {
              const allowed = grantable.has(permission.code);
              return (
                <label
                  key={permission.code}
                  className={`flex items-start gap-2 rounded-lg px-2 py-1.5 text-xs ${
                    allowed ? 'cursor-pointer hover:bg-raised' : 'cursor-not-allowed opacity-40'
                  }`}
                >
                  <input
                    type="checkbox"
                    disabled={!allowed}
                    checked={selected.has(permission.code)}
                    onChange={() => onToggle(permission.code)}
                    className="mt-0.5 size-5 shrink-0 accent-[var(--color-brand)]"
                  />
                  <span className="min-w-0">
                    <span className="tabular block text-ink">{permission.code}</span>
                    <span className="block text-[0.68rem] text-ink-faint">
                      {permissionText(permission.code, permission.description)}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function RoleEditor({ grantable }: { grantable: Set<string> }) {
  const { t } = useI18n();
  const { roleDescription } = useRoleText();
  const { data, isPending } = useRoles();
  const createRole = useCreateRole();
  const updateRole = useUpdateRole();
  const archiveRole = useArchiveRole();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<RoleRow | null>(null);
  const [form, setForm] = useState({ code: '', name: '', description: '' });
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const error = (createRole.error ?? updateRole.error ?? archiveRole.error);
  const errorText = useApiError();
  const message = errorText(error);

  const startCreate = () => {
    setEditing(null);
    setForm({ code: '', name: '', description: '' });
    setSelected(new Set());
    setCreating(true);
  };

  const startEdit = (role: RoleRow) => {
    setCreating(false);
    setEditing(role);
    setForm({ code: role.code, name: role.name, description: role.description ?? '' });
    setSelected(new Set(role.permissions));
  };

  const toggle = (code: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });

  const close = () => { setCreating(false); setEditing(null); };

  if (isPending) return <Skeleton className="h-48" />;

  return (
    <Card>
      <SectionTitle action={
        <Button variant="ghost" size="sm" onClick={creating ? close : startCreate}>
          {creating ? t('common.cancel') : t('staff.addPosition')}
        </Button>
      }>{t('staff.positions')}</SectionTitle>

      {message && <div className="mb-3"><Alert>{message}</Alert></div>}

      {(creating || editing) && (
        <form
          className="mb-4 space-y-3 rounded-xl border border-brand/30 bg-brand-wash/40 p-3"
          onSubmit={(event) => {
            event.preventDefault();
            const permissions = [...selected];
            if (editing) {
              updateRole.mutate({
                id: editing.id,
                name: form.name.trim(),
                description: form.description.trim() || null,
                ...(editing.code === 'OWNER' ? {} : { permissions }),
              }, { onSuccess: close });
            } else {
              createRole.mutate({
                code: form.code.trim().toUpperCase(),
                name: form.name.trim(),
                description: form.description.trim() || undefined,
                permissions,
              }, { onSuccess: close });
            }
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label={t('staff.code')}
              required
              disabled={Boolean(editing)}
              placeholder="DETAILER"
              value={form.code}
              onChange={(event) => setForm((p) => ({ ...p, code: event.target.value.toUpperCase() }))}
              hint={editing ? t('staff.codeLocked') : t('staff.codeHint')}
            />
            <Field
              label={t('staff.name')}
              required
              placeholder={t('staff.positionNamePlaceholder')}
              value={form.name}
              onChange={(event) => setForm((p) => ({ ...p, name: event.target.value }))}
            />
          </div>
          <Field
            label={t('staff.description')}
            placeholder={t('staff.descriptionPlaceholder')}
            value={form.description}
            onChange={(event) => setForm((p) => ({ ...p, description: event.target.value }))}
          />

          {editing?.code === 'OWNER' ? (
            <Alert tone="info">{t('staff.ownerLocked')}</Alert>
          ) : (
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-[0.68rem] font-semibold uppercase tracking-wider text-ink-faint">
                  {t('staff.permissionsCount', { count: selected.size })}
                </span>
              </div>
              <PermissionPicker selected={selected} onToggle={toggle} grantable={grantable} />
            </div>
          )}

          <div className="flex gap-2">
            <Button
              type="submit"
              className="flex-1"
              loading={createRole.isPending || updateRole.isPending}
              disabled={!form.name.trim() || (!editing && !form.code.trim())}
            >
              {editing ? t('staff.savePosition') : t('staff.createPosition')}
            </Button>
            <Button variant="ghost" type="button" onClick={close}>{t('common.cancel')}</Button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {data?.items.map((role) => (
          <div key={role.id} className="rounded-xl border border-line bg-raised/40 px-3 py-2.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <RoleChip role={role} />
                {role.isSystem && <Pill>{t('staff.builtIn')}</Pill>}
                <span className="text-xs text-ink-faint">
                  {t('staff.permissionsCount', { count: role.permissions.length })}
                  <span className="mx-1.5 opacity-50">·</span>
                  {t('staff.peopleCount', { count: role.staffCount })}
                </span>
              </div>
              <div className="flex shrink-0 gap-1.5">
                <Button variant="quiet" size="sm" onClick={() => startEdit(role)}>{t('common.edit')}</Button>
                {!role.isSystem && role.staffCount === 0 && (
                  <Button
                    variant="quiet"
                    size="sm"
                    loading={archiveRole.isPending}
                    onClick={() => archiveRole.mutate(role.id)}
                  >{t('common.remove')}</Button>
                )}
              </div>
            </div>
            {role.description && (
              <p className="mt-1 text-xs text-ink-dim">{roleDescription(role.code, role.description)}</p>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

function StaffForm({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const createStaff = useCreateStaff();
  const { data: roles } = useRoles();
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '', jobTitle: '', password: '',
  });
  const [canSignIn, setCanSignIn] = useState(true);
  const [roleIds, setRoleIds] = useState<Set<string>>(new Set());

  const errorText = useApiError();
  const error = createStaff.error instanceof ApiError ? createStaff.error : null;
  const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [key]: event.target.value }));

  return (
    <Card className="mb-4">
      <SectionTitle>{t('staff.newPerson')}</SectionTitle>
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          createStaff.mutate({
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            ...(form.email ? { email: form.email.trim() } : {}),
            ...(form.phone ? { phone: form.phone.trim() } : {}),
            ...(form.jobTitle ? { jobTitle: form.jobTitle.trim() } : {}),
            canSignIn,
            ...(canSignIn ? { password: form.password } : {}),
            roleIds: [...roleIds],
          }, { onSuccess: onDone });
        }}
      >
        {error && <Alert>{errorText(error) ?? error.message}</Alert>}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('common.firstName')} required value={form.firstName} onChange={set('firstName')} />
          <Field label={t('common.lastName')} required value={form.lastName} onChange={set('lastName')} />
          <Field label={t('staff.jobTitle')} placeholder={t('staff.jobTitlePlaceholder')} value={form.jobTitle} onChange={set('jobTitle')} />
          <Field label={t('common.phone')} inputMode="tel" value={form.phone} onChange={set('phone')} />
        </div>

        <label className="flex cursor-pointer items-start gap-2.5 rounded-xl border border-line bg-raised/40 px-3 py-2.5">
          <input
            type="checkbox"
            checked={canSignIn}
            onChange={(event) => setCanSignIn(event.target.checked)}
            className="mt-0.5 size-5 accent-[var(--color-brand)]"
          />
          <span className="text-sm">
            <span className="block text-ink">{t('staff.canSignIn')}</span>
          </span>
        </label>

        {canSignIn && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('common.email')} type="email" required value={form.email} onChange={set('email')} />
            <Field
              label={t('staff.password')}
              type="password"
              required
              minLength={12}
              value={form.password}
              onChange={set('password')}
              hint={t('staff.passwordHint')}
            />
          </div>
        )}

        <div>
          <div className="mb-1.5 text-[0.68rem] font-semibold uppercase tracking-wider text-ink-faint">{t('staff.positions')}</div>
          <div className="flex flex-wrap gap-1.5">
            {roles?.items.map((role) => {
              const active = roleIds.has(role.id);
              return (
                <button
                  key={role.id}
                  type="button"
                  onClick={() => setRoleIds((prev) => {
                    const next = new Set(prev);
                    if (next.has(role.id)) next.delete(role.id); else next.add(role.id);
                    return next;
                  })}
                  className={`rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                    active
                      ? 'border-brand bg-brand-wash text-brand'
                      : 'border-line bg-raised text-ink-dim hover:text-ink'
                  }`}
                >
                  {role.name}
                </button>
              );
            })}
          </div>
        </div>

        <Button
          type="submit"
          className="w-full"
          loading={createStaff.isPending}
          disabled={!form.firstName.trim() || !form.lastName.trim()
            || (canSignIn && (!form.email.trim() || form.password.length < 12))}
        >{t('staff.addPerson')}</Button>
      </form>
    </Card>
  );
}

function StaffRow({ person, roles }: { person: StaffMember; roles: RoleRow[] }) {
  const { t } = useI18n();
  const updateStaff = useUpdateStaff();
  const [open, setOpen] = useState(false);
  const [roleIds, setRoleIds] = useState<Set<string>>(
    new Set(roles.filter((r) => person.roles.some((pr) => pr.code === r.code)).map((r) => r.id)),
  );
  const [newPassword, setNewPassword] = useState('');

  return (
    <Card className={person.isActive ? '' : 'opacity-60'}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-ink">{person.firstName} {person.lastName}</span>
            {person.roles.map((role) => <RoleChip key={role.code} role={role} />)}
            {!person.canSignIn && <Pill>{t('staff.noLoginPill')}</Pill>}
            {!person.isActive && <Pill tone="danger">{t('staff.inactivePill')}</Pill>}
            {person.lockedUntil && <Pill tone="warning">{t('staff.lockedPill')}</Pill>}
          </div>
          <div className="mt-1 text-xs text-ink-faint">
            {person.jobTitle ?? '—'}
            {person.canSignIn && (
              <>
                <span className="mx-1.5 opacity-50">·</span>
                {person.email}
              </>
            )}
          </div>
          <div className="mt-0.5 text-[0.68rem] text-ink-faint">
            {person.lastLoginAt
              ? t('staff.lastSignedIn', { when: formatDateTime(person.lastLoginAt) })
              : person.canSignIn ? t('staff.neverSignedIn') : t('staff.workRecorded')}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-4">
          <div className="text-right">
            <Amount
              value={formatGel(person.work.valueTetri)}
              className="block text-sm font-semibold text-ink"
            />
            <div className="text-[0.62rem] uppercase tracking-wider text-ink-faint">
              {t('staff.jobs', { count: person.work.jobs })}
            </div>
          </div>
          <Button variant="quiet" size="sm" onClick={() => setOpen((v) => !v)}>
            {open ? t('staff.close') : t('staff.manage')}
          </Button>
        </div>
      </div>

      {open && (
        <div className="mt-3 space-y-3 border-t border-line pt-3">
          <div>
            <div className="mb-1.5 text-[0.68rem] font-semibold uppercase tracking-wider text-ink-faint">{t('staff.positions')}</div>
            <div className="flex flex-wrap gap-1.5">
              {roles.map((role) => {
                const active = roleIds.has(role.id);
                return (
                  <button
                    key={role.id}
                    onClick={() => setRoleIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(role.id)) next.delete(role.id); else next.add(role.id);
                      return next;
                    })}
                    className={`rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                      active
                        ? 'border-brand bg-brand-wash text-brand'
                        : 'border-line bg-raised text-ink-dim hover:text-ink'
                    }`}
                  >
                    {role.name}
                  </button>
                );
              })}
            </div>
          </div>

          {person.canSignIn && (
            <Field
              label={t('staff.resetPassword')}
              type="password"
              minLength={12}
              placeholder={t('staff.passwordKeep')}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              hint={t('staff.resetHint')}
            />
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              loading={updateStaff.isPending}
              onClick={() => updateStaff.mutate({
                id: person.id,
                roleIds: [...roleIds],
                ...(newPassword.length >= 12 ? { newPassword } : {}),
              }, { onSuccess: () => { setNewPassword(''); setOpen(false); } })}
            >{t('common.save')}</Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => updateStaff.mutate({ id: person.id, isActive: !person.isActive })}
            >
              {person.isActive ? t('common.deactivate') : t('common.reactivate')}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export function StaffPage() {
  const { t } = useI18n();
  const { data: session } = useSession();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [adding, setAdding] = useState(false);
  const { data: staff, isPending } = useStaff(includeInactive);
  const { data: roles } = useRoles();

  const mayCreate = can(session, 'staff.create');
  const grantable = new Set(session?.permissions ?? []);

  return (
    <div className="mx-auto max-w-4xl">
      <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-[1.75rem] font-bold leading-tight tracking-tight text-ink sm:text-[2rem]">{t('staff.title')}</h1>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex min-h-11 cursor-pointer items-center gap-2 text-xs text-ink-dim">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(event) => setIncludeInactive(event.target.checked)}
              className="size-5 accent-[var(--color-brand)]"
            />{t('staff.showInactive')}</label>
          {mayCreate && (
            <Button variant={adding ? 'ghost' : 'primary'} onClick={() => setAdding((v) => !v)}>
              {adding ? t('common.cancel') : t('staff.addPerson')}
            </Button>
          )}
        </div>
      </header>

      {adding && <StaffForm onDone={() => setAdding(false)} />}

      {isPending && (
        <div className="space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}</div>
      )}

      {!isPending && staff?.items.length === 0 && <EmptyState title={t('staff.empty')} />}

      <div className="mb-6 space-y-2">
        {staff?.items.map((person) => (
          <StaffRow key={person.id} person={person} roles={roles?.items ?? []} />
        ))}
      </div>

      {can(session, 'staff.read') && <RoleEditor grantable={grantable} />}
    </div>
  );
}
